import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '@/lib/budget-utils';
import type { BurnRateDataPoint } from '@/types/budget.types';

interface BurnRateChartProps {
  data: BurnRateDataPoint[];
  title?: string;
  height?: number;
  budgetTotal?: number;
}

export function BurnRateChart({ 
  data, 
  title = 'Burn Rate',
  height = 300,
  budgetTotal,
}: BurnRateChartProps) {
  // Map data to chart format, handling both old and new property names
  const chartData = data.map(d => ({
    period: d.period || d.date,
    budgetPlan: d.budgetPlan ?? d.budgetCumulative,
    actualCost: d.actualCost ?? d.actualCumulative,
    forecast: d.forecastCumulative,
  }));
  
  const todayIndex = chartData.findIndex((_, i) => i === Math.floor(chartData.length / 2));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-lg border bg-background p-3 shadow-md">
          <p className="font-medium mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            {payload.map((entry: any, index: number) => (
              <div key={index} className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-2">
                  <span 
                    className="w-3 h-3 rounded" 
                    style={{ backgroundColor: entry.color }} 
                  />
                  {entry.name}:
                </span>
                <span className="font-medium">{formatCurrency(entry.value)}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="period" 
              className="text-xs"
              tick={{ fill: 'currentColor' }}
            />
            <YAxis 
              className="text-xs"
              tick={{ fill: 'currentColor' }}
              tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />
            
            {/* Budget line (planned spend) */}
            <Line
              type="monotone"
              dataKey="budgetPlan"
              stroke="#94a3b8"
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Budget Plan"
              dot={false}
            />
            
            {/* Actual cost line */}
            <Line
              type="monotone"
              dataKey="actualCost"
              stroke="#3b82f6"
              strokeWidth={2}
              name="Actual Cost"
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
            
            {/* Forecast line */}
            <Line
              type="monotone"
              dataKey="forecast"
              stroke="#f59e0b"
              strokeWidth={2}
              strokeDasharray="3 3"
              name="Forecast"
              dot={false}
            />

            {/* Today marker */}
            {todayIndex >= 0 && (
              <ReferenceLine
                x={chartData[todayIndex].period}
                stroke="#64748b"
                strokeDasharray="3 3"
                label={{ value: 'Today', position: 'top', fill: '#64748b' }}
              />
            )}

            {/* Budget total line */}
            {budgetTotal && (
              <ReferenceLine
                y={budgetTotal}
                stroke="#ef4444"
                strokeDasharray="3 3"
                label={{ value: 'Budget Limit', position: 'right', fill: '#ef4444' }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
